import { AgGridAngular } from '@ag-grid-community/angular';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
} from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';

import { EdcMappingNoRowsUploadComponent } from './edc-mapping-no-rows-upload.component';
import fakeResult from './sampleResp.json';

interface EdcMappingData {
  mappings: EdcMappingRow[];
}

interface EdcMappingRow {
  auxiliusColumnName: string;
  edcColumnName: string;
  file: string;
  includes: string;
  inputCount?: number;
  notes: string;
  outputCount?: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridAngular, ReactiveFormsModule, IconComponent],
  selector: 'aux-edc-mapping',
  styleUrl: 'edc-mapping.component.scss',
  templateUrl: './edc-mapping.component.html',
})
export class EdcMappingComponent implements OnInit {
  private authQuery = inject(AuthQuery);
  private gqlService = inject(GqlService);
  private overlayService = inject(OverlayService);

  // eslint-disable-next-line max-lines-per-function
  getDefaultColumnDefs = (): ColDef[] => {
    return [
      {
        autoHeight: true,
        cellStyle: { fontWeight: 'bold' },
        field: 'file',
        headerName: 'File',
        hide: true,
        pinned: 'left',
        rowGroup: true,
        sort: 'asc',
      },
      {
        autoHeight: true,
        field: 'includes',
        headerName: 'Includes',
        hide: true,
        rowGroup: true,
        sort: 'asc',
        wrapText: true,
      },
      {
        autoHeight: true,
        field: 'edcColumnName',
        headerName: 'EDC: Column Name',
        tooltipField: 'edcColumnName',
      },
      {
        autoHeight: true,
        field: 'auxiliusColumnName',
        headerName: 'Auxilius: Column Name',
        tooltipField: 'auxiliusColumnName',
      },
      {
        cellRenderer: (params: ICellRendererParams) => {
          return params.value != null ? params.value : '–';
        },
        comparator: (valueA?: number, valueB?: number) => {
          const a = valueA ?? -1;
          const b = valueB ?? -1;

          return a - b;
        },
        field: 'inputCount',
        flex: 0.6,
        headerName: 'Input count',
        type: 'numericColumn',
      },
      {
        cellRenderer: (params: ICellRendererParams) => {
          return params.value != null ? params.value : '–';
        },
        comparator: (valueA?: number, valueB?: number) => {
          const a = valueA ?? -1;
          const b = valueB ?? -1;

          return a - b;
        },
        field: 'outputCount',
        flex: 0.6,
        headerName: 'Output count',
        type: 'numericColumn',
      },
      {
        cellRenderer: (params: ICellRendererParams) => {
          return params.value || '–';
        },
        field: 'notes',
        flex: 2,
        headerName: 'Notes',
        // minWidth: 150,
        tooltipField: 'notes',
        wrapText: true,
      },
    ];
  };

  edcMappingColumnDefs: WritableSignal<ColDef[]> = signal(this.getDefaultColumnDefs());
  edcMappingData = signal<EdcMappingData | null>(null);
  gridAPI = signal<GridApi<EdcMappingRow> | null>(null);
  isProcessing = signal(false);

  gridOptions = computed<GridOptions>(() => {
    const cdc = this.edcMappingColumnDefs();

    return {
      columnDefs: cdc,
      rowData: this.edcMappingData()?.mappings || [],
      suppressRowTransform: true,
    };
  });

  @ViewChild('fileManager') fileManager?: FileManagerComponent;
  noRowsUploadComponent = EdcMappingNoRowsUploadComponent;

  ngOnInit() {
    this.loadEdcMapping().catch(console.error);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  edcMappingRow = (mapping: any) => ({
    auxiliusColumnName: mapping.auxiliusColumnName,
    edcColumnName: mapping.edcColumnName,
    file: mapping.file,
    includes: mapping.includes,
    inputCount: mapping.inputCount,
    notes: mapping.notes,
    outputCount: mapping.outputCount,
  });

  private async loadEdcMapping() {
    try {
      const result = fakeResult.getEdcMapping;

      // await this.gqlService.getEdcMapping$({
      // trial_id: this.authQuery.getTrialId(),
      // })
      // .toPromise();

      if (result?.success && result.mappings?.length > 0) {
        this.edcMappingData.set({
          mappings: result.mappings.map(this.edcMappingRow),
        });
        this.edcMappingColumnDefs.set(this.getDefaultColumnDefs());
      } else {
        this.edcMappingData.set(null);
      }
    } catch (error) {
      console.error('Error loading EDC mapping:', error);
      this.edcMappingData.set(null);
    }
  }

  onGridReady(event: GridReadyEvent<EdcMappingRow>) {
    this.gridAPI.set(event.api);
    event.api.sizeColumnsToFit();
  }

  openDialog() {
    const popupRef = this.overlayService.openPopup({
      content: EdcMappingNoRowsUploadComponent,
      settings: {
        header: 'Upload EDC Mapping',
        primaryButton: {
          action: (instance: EdcMappingNoRowsUploadComponent | null) => {
            instance
              ?.onUpload()
              .then((success: boolean) => {
                if (success) {
                  popupRef.close();
                  this.loadEdcMapping().catch(console.error);
                }
              })
              .catch(console.error);
          },
          label: 'Upload',
        },
      },
    });
  }
}
